// src/components/common/ProtectedRoute.tsx
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '../../features/auth/hooks/useAuth';
import { CircularProgress, Box, Alert } from '@mui/material';
import { shouldBypassAuth } from '../../config/devMode';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const devBypass = shouldBypassAuth();

  // Add debug logging to understand authentication state
  useEffect(() => {
    console.log('ProtectedRoute - Auth state:', { isAuthenticated, isLoading, devBypass });
  }, [isAuthenticated, isLoading, devBypass]);

  // Dev mode bypass - skip auth checks entirely in local development
  if (devBypass) {
    return (
      <>
        <Alert
          severity="warning"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            boxShadow: 3,
          }}
        >
          DEV MODE: Auth bypassed
        </Alert>
        {children}
      </>
    );
  }

  // If still loading authentication state, show loading indicator
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('User not authenticated, redirecting to login');
    // Save the path user was trying to access
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If authenticated, render children
  return <>{children}</>;
};

export default ProtectedRoute;